import { useEffect, useState } from 'react'
import './IMacHealth.css'

const API = 'https://status.myhubmain.com/health'
const POLL_MS = 10 * 60 * 1000 // 10 min

type Services = Record<string, string>

interface Health {
  host: string
  cpu_load_1m: number
  cpu_load_5m: number
  cpu_cores: number
  cpu_temp_c: number | null
  ram_used_gb: number
  ram_total_gb: number
  disk_used_gb: number
  disk_total_gb: number
  disk_used_pct: number
  uptime_days: number
  services: Services
  updated_at?: string
}

function colorFor(pct: number): string {
  if (pct >= 90) return 'crit'
  if (pct >= 75) return 'warn'
  return 'ok'
}

function tempColor(t: number | null): string {
  if (t == null) return 'unknown'
  if (t >= 85) return 'crit'
  if (t >= 75) return 'warn'
  return 'ok'
}

function serviceStatus(s: string): 'ok' | 'warn' | 'crit' {
  if (s === 'active' || s === 'healthy') return 'ok'
  if (s === 'starting' || s === 'unhealthy') return 'warn'
  return 'crit'
}

function formatAgo(iso: string): { text: string; stale: boolean } {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, now - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return { text: 'agora mesmo', stale: false }
  if (mins < 60) return { text: `há ${mins} min`, stale: mins > 15 }
  const hours = Math.floor(mins / 60)
  if (hours < 24) return { text: `há ${hours}h`, stale: true }
  return { text: `há ${Math.floor(hours / 24)}d`, stale: true }
}

export default function IMacHealth() {
  const [data, setData] = useState<Health | null>(null)
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let interval: number | undefined
    let cancelled = false

    const fetchHealth = async () => {
      try {
        const r = await fetch(API, {
          cache: 'no-store',
          credentials: 'include',
          redirect: 'error',
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const j = (await r.json()) as Health
        if (!cancelled) {
          setData(j)
          setFetchedAt(new Date().toISOString())
          setError(null)
          setLoading(false)
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'erro'
          // CF Access redireciona para login -> fetch falha com TypeError
          setError(msg.includes('Failed to fetch') || msg.includes('redirect') ? 'auth' : msg)
          setLoading(false)
        }
      }
    }

    const start = () => {
      if (document.visibilityState !== 'visible') return
      fetchHealth()
      interval = window.setInterval(fetchHealth, POLL_MS)
    }
    const stop = () => {
      if (interval) {
        window.clearInterval(interval)
        interval = undefined
      }
    }
    const onVis = () => {
      stop()
      start()
    }

    start()
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelled = true
      stop()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  if (loading) {
    return (
      <div className="imac-health loading">
        <p>A ligar ao iMac...</p>
      </div>
    )
  }

  if (error === 'auth') {
    return (
      <div className="imac-health locked">
        <p>🔒 Dashboard privado</p>
        <a
          href="https://status.myhubmain.com/health"
          target="_blank"
          rel="noopener noreferrer"
          className="login-btn"
        >
          Fazer login
        </a>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="imac-health error">
        <p>⚠️ Sem dados do iMac ({error || 'vazio'}).</p>
      </div>
    )
  }

  const ramPct = data.ram_total_gb
    ? Math.round((data.ram_used_gb / data.ram_total_gb) * 100)
    : 0
  const loadPct = data.cpu_cores
    ? Math.min(100, Math.round((data.cpu_load_1m / data.cpu_cores) * 100))
    : 0
  const ago = formatAgo(fetchedAt ?? data.updated_at ?? new Date().toISOString())

  const serviceLabels: Record<string, string> = {
    paperless: 'Paperless',
    immich: 'Immich',
    jellyfin: 'Jellyfin',
    train: 'AI Treino',
    vectorize: 'AI Vectorize',
  }

  return (
    <div className="imac-health">
      <div className="imac-header">
        <h3>
          <span className={`dot ${ago.stale ? 'crit' : 'ok'}`} /> iMac
        </h3>
        <span className={`last-update ${ago.stale ? 'stale' : ''}`}>
          {ago.stale ? '⚠️ ' : ''}atualizado {ago.text}
        </span>
      </div>

      <div className="services">
        {Object.entries(data.services).map(([key, val]) => (
          <div key={key} className="service">
            <span className={`dot ${serviceStatus(val)}`} />
            <span className="name">{serviceLabels[key] || key}</span>
            <span className="status">{val}</span>
          </div>
        ))}
      </div>

      <div className="bars">
        <div className="bar-row">
          <span className="label">CPU</span>
          <div className="bar">
            <div className={`fill ${colorFor(loadPct)}`} style={{ width: `${loadPct}%` }} />
          </div>
          <span className="value">
            {data.cpu_load_1m.toFixed(2)} / {data.cpu_cores} cores
          </span>
        </div>
        <div className="bar-row">
          <span className="label">RAM</span>
          <div className="bar">
            <div className={`fill ${colorFor(ramPct)}`} style={{ width: `${ramPct}%` }} />
          </div>
          <span className="value">
            {data.ram_used_gb.toFixed(1)} / {data.ram_total_gb.toFixed(0)} GB ({ramPct}%)
          </span>
        </div>
        <div className="bar-row">
          <span className="label">Disco</span>
          <div className="bar">
            <div
              className={`fill ${colorFor(data.disk_used_pct)}`}
              style={{ width: `${data.disk_used_pct}%` }}
            />
          </div>
          <span className="value">
            {data.disk_used_gb.toFixed(0)} / {data.disk_total_gb.toFixed(0)} GB ({data.disk_used_pct}%)
          </span>
        </div>
      </div>

      <div className="meta">
        <span>
          🌡️{' '}
          <span className={`temp ${tempColor(data.cpu_temp_c)}`}>
            {data.cpu_temp_c != null ? `${data.cpu_temp_c.toFixed(0)}°C` : '—'}
          </span>
        </span>
        <span>⏱️ uptime {data.uptime_days}d</span>
      </div>
    </div>
  )
}
